import { vrActivitiesService } from "src/serviceHandlers/vrActivitiesService";
import { serviceCodesService } from "src/serviceHandlers/serviceCodesService";

// Service codes with bespoke pickers that are NOT part of the generic VR
// activity system: JC (two-list, job_coaching_activities table) and the Job
// Development codes (free-text fields).
const JD_CODES = ["CE", "SE", "SJBT", "TWE"];
const EXCLUDED_CODES = ["JC", ...JD_CODES];

// Legacy per-code columns, used as a read fallback for un-backfilled shifts.
const LEGACY_COLUMNS = { LS: "ls_activities", SED: "sed_activities", SB: "sb_activities" };

// Tolerant parse of the generic store into a plain object.
function parseSelectedObject(value) {
  if (value == null) return {};
  let v = value;
  for (let i = 0; i < 2 && typeof v === "string"; i++) {
    try {
      v = JSON.parse(v);
    } catch {
      return {};
    }
  }
  return v && typeof v === "object" && !Array.isArray(v) ? v : {};
}

// Tolerant parse of a value that should be an array of names.
function toArray(value) {
  if (value == null) return [];
  let v = value;
  for (let i = 0; i < 2 && typeof v === "string"; i++) {
    try {
      v = JSON.parse(v);
    } catch {
      return v.trim() ? [v.trim()] : [];
    }
  }
  return Array.isArray(v) ? v.filter((x) => x != null && `${x}`.trim()).map(String) : [];
}

// Shared logic for the generic, service-code-keyed VR "Activity Completed"
// pickers. Consuming components must implement getShiftVrCodes() to return the
// list of service-code strings on the current shift.
export default {
  data() {
    return {
      // { [code]: { options: [{id,name}], selectedIds: [], name, max } }
      vrActivityState: {},
      vrActivitiesLoading: false,
    };
  },
  computed: {
    // VR codes on the shift that have a non-empty admin-managed activity list.
    vrCodesWithActivities() {
      return Object.keys(this.vrActivityState);
    },
  },
  methods: {
    // Override in the component. Returns service-code strings on the shift.
    getShiftVrCodes() {
      return [];
    },

    isGenericVrCode(code) {
      return !!code && !EXCLUDED_CODES.includes(code);
    },

    maxForCode(code) {
      const st = this.vrActivityState[code];
      return st ? st.max : 3;
    },

    // Fetch option lists + per-code config for the shift's generic VR codes.
    // Only codes with a non-empty list end up in vrActivityState (and thus
    // render a picker).
    async loadVrActivities() {
      const codes = (this.getShiftVrCodes() || []).filter((c) =>
        this.isGenericVrCode(c),
      );
      if (!codes.length) {
        this.$set(this, "vrActivityState", {});
        return;
      }
      this.vrActivitiesLoading = true;
      try {
        const vrCodes = await serviceCodesService.listVR();
        const meta = {};
        (vrCodes || []).forEach((sc) => {
          meta[sc.code] = sc;
        });
        const state = {};
        await Promise.all(
          [...new Set(codes)].map(async (code) => {
            const options = await vrActivitiesService.listByServiceCode(code);
            if (!options || options.length === 0) return;
            const sc = meta[code] || {};
            state[code] = {
              options,
              selectedIds: [],
              name: sc.name || code,
              max:
                sc.max_activities_select == null
                  ? null
                  : Number(sc.max_activities_select),
            };
          }),
        );
        this.$set(this, "vrActivityState", state);
      } catch (e) {
        if (this.$notifyError) this.$notifyError("Failed to load activity options");
      } finally {
        this.vrActivitiesLoading = false;
      }
    },

    // Map a shift's saved activity NAMES back to option ids per code. Reads the
    // generic vr_activities_selected with a legacy per-code-column fallback.
    loadSelectedVrActivities(shift) {
      if (!shift) return;
      const data = shift.originalData || shift;
      const stored = parseSelectedObject(data.vr_activities_selected);
      Object.keys(this.vrActivityState).forEach((code) => {
        const st = this.vrActivityState[code];
        let names = [];
        if (Object.prototype.hasOwnProperty.call(stored, code)) {
          names = toArray(stored[code]);
        } else if (LEGACY_COLUMNS[code]) {
          names = toArray(data[LEGACY_COLUMNS[code]]);
        }
        const ids = names
          .map((n) => (st.options.find((o) => o.name === n) || {}).id)
          .filter((id) => id != null);
        this.$set(st, "selectedIds", ids);
      });
    },

    onVrActivitySelect(code, ids) {
      const st = this.vrActivityState[code];
      if (!st) return;
      let next = Array.isArray(ids) ? ids : [];
      if (st.max != null && next.length > st.max) next = next.slice(0, st.max);
      this.$set(st, "selectedIds", next);
    },

    // Build { code: [names] } from current selections (omits empty codes).
    buildVrActivitiesPayload() {
      const out = {};
      Object.keys(this.vrActivityState).forEach((code) => {
        const st = this.vrActivityState[code];
        const names = (st.selectedIds || [])
          .map((id) => (st.options.find((o) => o.id === id) || {}).name)
          .filter(Boolean);
        if (names.length) out[code] = names;
      });
      return out;
    },

    // Merge current selections into an existing stored object, so editing a
    // single code on a multi-code shift doesn't wipe the other codes. Used by
    // the single-code EditShiftNotes flow.
    mergeVrActivitiesPayload(existingRaw) {
      const result = { ...parseSelectedObject(existingRaw) };
      Object.keys(this.vrActivityState).forEach((code) => {
        const st = this.vrActivityState[code];
        const names = (st.selectedIds || [])
          .map((id) => (st.options.find((o) => o.id === id) || {}).name)
          .filter(Boolean);
        if (names.length) result[code] = names;
        else delete result[code];
      });
      return result;
    },

    // Returns an error string if any code with a list has no selection (used by
    // clock-out / approve-missed which require activities).
    validateVrActivities({ requireMin } = {}) {
      if (!requireMin) return null;
      for (const code of this.vrCodesWithActivities) {
        const st = this.vrActivityState[code];
        if (!st.selectedIds || st.selectedIds.length === 0) {
          return `Please select at least one activity for ${st.name || code}`;
        }
      }
      return null;
    },
  },
};
